import React, { useEffect, useState } from 'react';
import { History, Location } from 'history';
import queryString from 'query-string';

import { PageLoadingIndicator } from 'shared/Spinner';
import { COLOR_MUTED_BACKGROUND } from 'components/PaywallsShared/constants';
import { AnchorAPI } from 'modules/AnchorAPI';
import { Metadata } from 'types/Metadata';
import { Button } from 'shared/Button/NewButton';
import { WarningIcon } from 'shared/WarningIcon';
import { getPaywallsSubscribeUrl } from 'components/PaywallsShared/utils';
import { AccessibleErrorContainer } from 'components/AccessibleErrorContainer';
import { useT } from 'i18n/hooks/useT';
import {
  ButtonContainer,
  CancelContainer,
  ContentContainer,
  Header,
  PodcastName,
  StyledWarningMessage,
} from './styles';

export const ERROR_ROUTE = '/404';

interface PaywallsCancelSubscriptionProps {
  history: History;
  location: Location;
  match: {
    params: {
      vanitySlug: string;
    };
  };
}

enum STATUS {
  IDLE = 'IDLE',
  CANCELLING = 'CANCELLING',
  CANCELLED = 'CANCELLED',
  ERROR = 'ERROR',
}

export const PaywallsCancelSubscription = ({
  history,
  location: { search },
  match: {
    params: { vanitySlug },
  },
}: PaywallsCancelSubscriptionProps) => {
  const [metadata, setMetadata] = useState<Metadata | null>(null);
  const [webStationId, setWebStationId] = useState<string | null>(null);
  const [status, setStatus] = useState<STATUS>(STATUS.IDLE);
  const t = useT();

  const { code } = queryString.parse(search);
  const forwardTo404 = () => history.push(ERROR_ROUTE);

  useEffect(() => {
    const getData = async () => {
      try {
        const stationId = await AnchorAPI.getStationIdForVanityName(vanitySlug);
        setWebStationId(stationId);
        const metaResponse = await AnchorAPI.getPodcastMetadata(stationId);
        if (!metaResponse?.stationPaywall?.isSubscriptionEnabled) {
          return forwardTo404();
        }
        setMetadata(metaResponse);
      } catch (err) {
        forwardTo404();
      }
      return null;
    };
    if (vanitySlug) {
      getData();
    }
  }, [vanitySlug]);

  if (!code || typeof code !== 'string' || !vanitySlug) {
    forwardTo404();
    return null;
  }

  if (!metadata || !webStationId) {
    return <PageLoadingIndicator color={COLOR_MUTED_BACKGROUND} />;
  }

  const { podcastName } = metadata;
  const subscribeUrl = getPaywallsSubscribeUrl(vanitySlug);

  if (status === STATUS.CANCELLED) {
    return (
      <CancelContainer>
        <Header>
          {t(
            'PaywallsCancelSubscription.CancellationConfirmationHeader',
            'Subscription canceled',
            'Subscription cancellation confirmation header'
          )}
        </Header>
        <ContentContainer>
          {t(
            'PaywallsCancelSubscription.CancellationConfirmationMessage',
            'You won’t be charged for {podcastName} going forward. If you’d like to subscribe to {podcastName} in the future, you can sign up from their profile at {subscribeUrl}',
            'Subscription cancellation confirmation message',
            {
              podcastName: <PodcastName>{podcastName}</PodcastName>,
              subscribeUrl: (
                <a
                  href={subscribeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {subscribeUrl}
                </a>
              ),
            }
          )}
          .
        </ContentContainer>
      </CancelContainer>
    );
  }

  return (
    <CancelContainer>
      <Header>
        {t(
          'PaywallsCancelSubscription.CancellationHeader',
          'Would you like to cancel your subscription?',
          'Subscription cancellation header'
        )}
      </Header>
      <ContentContainer>
        {t(
          'PaywallsCancelSubscription.CancellationMessage',
          'If you cancel, you can access your subscription to {podcastName} until the next billing period.',
          'Subscription cancellation disclaimer',
          { podcastName: <PodcastName>{podcastName}</PodcastName> }
        )}
      </ContentContainer>
      <ButtonContainer>
        <Button
          kind="button"
          color="red"
          isDisabled={status === STATUS.CANCELLING}
          onClick={async () => {
            setStatus(STATUS.CANCELLING);
            try {
              await AnchorAPI.cancelPodcastSubscription({
                code,
                webStationId,
              });
              setStatus(STATUS.CANCELLED);
            } catch (err) {
              setStatus(STATUS.ERROR);
            }
          }}
        >
          {status === STATUS.CANCELLING
            ? t(
                'PaywallsCancelSubscription.CancelInProgressButtonLabel',
                'Cancelling subscription...',
                'Cancel subscription in progress button label'
              )
            : t(
                'PaywallsCancelSubscription.CancelButtonLabel',
                'Cancel subscription',
                'Cancel subscription button label'
              )}
        </Button>
      </ButtonContainer>
      {status === STATUS.ERROR && (
        <AccessibleErrorContainer>
          <StyledWarningMessage>
            <WarningIcon />{' '}
            {t(
              'PaywallsCancelSubscription.CancellationErrorMessage',
              'Something went wrong. Please submit again.',
              'Subscription cancellation error message'
            )}
          </StyledWarningMessage>
        </AccessibleErrorContainer>
      )}
    </CancelContainer>
  );
};
